<!-- src/components/DrawerHost.vue -->
<template>
  <v-navigation-drawer
    v-model="isOpen"
    :location="state.options.location"
    :width="state.options.width"
    :temporary="!state.options.persistent"
    :scrim="!state.options.persistent"
    class="drawer-below-header"
  >
    <component
      v-if="resolvedComponent"
      ref="componentRef"
      :is="resolvedComponent"
      v-bind="state.props"
      :drawer="api"
      @close="close"
    />
  </v-navigation-drawer>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted, watch } from 'vue'
import type { Component } from 'vue'
import { setDrawerHost, type DrawerOptions, type DrawerPayload } from '@/lib/composable/drawer/drawerHostBridge'

const componentRef = ref<any>(null)

type ResolvedOptions = Required<DrawerOptions>

const DEFAULTS: ResolvedOptions = {
  width: 420,
  persistent: false,
  location: 'end'
}

const isOpen = ref(false)
const registry = new Map<string, Component>()
const wasOpen = ref(false)

const state = reactive<{
  component: string | Component | null
  props: Record<string, any>
  options: ResolvedOptions
  onClose?: () => void
}>({
  component: null,
  props: {},
  options: { ...DEFAULTS }
})

function resolve(c: string | Component | null): Component | null {
  if (!c) return null
  if (typeof c === 'string') return registry.get(c) ?? null
  return c
}

const resolvedComponent = computed(() => resolve(state.component))

function open(payload: DrawerPayload) {
  state.component = payload.component
  state.props = payload.props ?? {}
  state.options = { ...DEFAULTS, ...(payload.options ?? {}) }
  state.onClose = payload.onClose
  isOpen.value = true
  wasOpen.value = true
}

function close() {
  if (!isOpen.value) return
  isOpen.value = false
  wasOpen.value = false
  // call onClose after drawer animation completes
  requestAnimationFrame(() => setTimeout(() => state.onClose?.(), 0))
}

// Watch for drawer closing via Escape key or other external means
watch(isOpen, (newValue: boolean, oldValue: boolean) => {
  // If drawer was open and is now closed (e.g., via Escape key)
  if (wasOpen.value && oldValue === true && newValue === false) {
    wasOpen.value = false
    // call onClose after drawer animation completes
    requestAnimationFrame(() => setTimeout(() => state.onClose?.(), 0))
  }
  // Update wasOpen to track current state
  if (newValue === true) {
    wasOpen.value = true
  }
})

function register(name: string, comp: Component) {
  registry.set(name, comp)
}

function unregister(name: string) {
  registry.delete(name)
}

function getComponentInstance() {
  return componentRef.value
}

const api = { open, close, register, unregister, getComponentInstance }
defineExpose(api)

onMounted(() => {
  // Publish API to the shared bridge (no window usage)
  setDrawerHost(api)
})
</script>

<style lang="scss" scoped>
.drawer-below-header {
	top: 64px !important;
	height: calc(100vh - 64px) !important;
	& + .v-navigation-drawer__scrim {
		top: 64px !important;
		height: calc(100vh - 64px) !important;
	}
}
</style>